<template>
  <div v-if="reportLoaded">
    <div class="d-flex flex-lg-row-reverse mt-1" v-if="this.numCustomers > 1">
      <h5 class="d-flex align-center">
        <span style="padding-top: .2rem">{{ $t('combine_all_results') }}</span>
        <v-switch class="ml-3" v-model="combineCustomers" inset/>
      </h5>
    </div>
    <div v-if="combineCustomers">
      <fleet-health-report-card
        class="mb-5"
        :customer-report="combinedReport"
        :start-date="startDate"
        :end-date="endDate"/>
    </div>
    <div v-else>
      <fleet-health-report-card
        v-for="(customerReport, index) of report.results" :key="index"
        class="mb-5"
        :customer-report="customerReport"
        :start-date="startDate"
        :end-date="endDate"/>
    </div>
  </div>
</template>

<script>
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ReportRecord from '@/orm/models/ReportRecord'
import FleetHealthReportCard from '@/components/report/fleet-health/FleetHealthReportCard'
import { get, set, each, mergeWith, max, map, size } from 'lodash'

export default {
  name: 'FleetHealthReport',
  components: {
    FleetHealthReportCard
  },
  mixins: [
    CurrentUserMixin
  ],
  props: {
    report: {
      type: ReportRecord
    }
  },
  data () {
    return {
      combineCustomers: false
    }
  },
  computed: {
    numCustomers () {
      return size(this.report.results)
    },
    combinedReport () {
      const results = {}
      const sumFields = [
        'gps_failure',
        'num_healthy',
        'num_unhealthy',
        'hot_violations',
        'cold_violations',
        'marked_as_fixed',
        'sensors_swapped',
        'cooler_sensor_failure',
        'freezer_sensor_failure',
        'excluded_from_dashboard',
        'cooler_temperature_health',
        'freezer_temperature_health',
        // we'll divide the average issue by the number of customers in the report later, for now get the sum.
        'average_issue_age_in_days'
      ]
      each(this.report.results, customer => {
        // Merge each customer's daily history together
        mergeWith(results, customer.insights_dashboard_history, (dest, src) => {
          // Sum all the fields in the array above.
          each(sumFields, field => {
            set(dest, field, get(src, field, 0) + get(dest, field, 0))
          })
          // For max issue age, use whichever issue age is higher
          const field = 'max_issue_age_in_days'
          set(dest, field, max([get(src, field), get(dest, field)]))
          return dest
        })
      })

      each(results, result => {
        // Recalculate the healthy percentage from the combined results.
        const numHealthy = get(result, 'num_healthy', 0)
        const numUnHealthy = get(result, 'num_unhealthy', 0)
        const total = numHealthy + numUnHealthy
        if (total > 0) {
          set(result, 'percent_healthy', (numHealthy / total * 100).toFixed(2))
        }
        // Recalculate the average issue age
        const newAverage = get(result, 'average_issue_age_in_days', 0) / this.numCustomers
        set(result, 'average_issue_age_in_days', newAverage)
      })

      const customerNames = map(this.report.results, x => x.name)
      return {
        name: customerNames.join(', ') + ' (' + this.$t('combined') + ')',
        insights_dashboard_history: results
      }
    },
    /**
     * Identifies if the report is loaded
     * @return boolean
     */
    reportLoaded () {
      return this.report && this.report.results_generated != null
    },
    /**
     * @return array
     */
    reportResults () {
      return Object.values(get(this, 'report.results') || {})
    },
    /**
     * The start date of the report.
     */
    startDate () {
      return get(this, 'report.parameters.startDate')
    },
    /**
     * The end date of the report.
     */
    endDate () {
      return get(this, 'report.parameters.endDate')
    }
  }
}
</script>

<style scoped>

</style>
